import { mergeFieldValues } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldValue,
  FieldValues,
  getComponentSubtree,
  setComponentSubtree,
  stripComponents,
} from 'features/libra/ontology/ontologyFieldTypes'
import omit from 'lodash/omit'

import { omitOrigin, resolveFieldOriginsForSave } from './fieldOrigin'
import type { FieldAccessScopePath } from './useScopedFieldAccess'

/**
 * The ONE place a scoped fieldValues merge is computed. Both writers --
 * `TicketAccessContext.setFieldValues` (ticket scope) and
 * `useScopedFieldAccess` (component scope) -- delegate here, so the
 * reserved-subtree preservation rules cannot drift between them.
 *
 * Ticket scope: strips the reserved `components` subtree from BOTH sides
 * before merging (a caller accidentally passing a `components` key at the
 * top level cannot stomp per-component state; `mergeFieldValues` treats
 * every top-level key as a `FieldValue` and would corrupt the nested map),
 * then re-attaches the existing subtree untouched.
 *
 * Component scope: merges into only that component's slice, materializing
 * the entry on first write. `mergeFieldValues` preserves wrapper metadata
 * (source citation, llmValue lineage) that a plain spread would drop.
 *
 * Both scopes stamp `updateSource`/`updatedAt` on the incoming values first
 * (`resolveFieldOriginsForSave`) and strip transient UI state from the merged result,
 * because the merge would otherwise restore a persisted copy of it.
 */
export function buildFieldValuesPatch(
  latestFieldValues: FieldValues,
  scope: FieldAccessScopePath,
  values: Partial<FieldValues>
): FieldValues {
  if (scope.kind === 'ticket') {
    const existingComponents = getComponentSubtree(latestFieldValues)
    const latestTopLevel = stripComponents(latestFieldValues)
    const mergedTopLevel = omitTransientFieldState(
      mergeFieldValues(
        latestTopLevel,
        resolveFieldOriginsForSave(
          latestTopLevel,
          stripComponents(values as FieldValues)
        )
      )
    )
    return existingComponents
      ? setComponentSubtree(mergedTopLevel, existingComponents)
      : mergedTopLevel
  }

  const { componentId } = scope
  const existingComponents = getComponentSubtree(latestFieldValues) ?? {}
  const latestComponentFields = existingComponents[componentId] ?? {}
  const mergedComponentFields = omitTransientFieldState(
    mergeFieldValues(
      latestComponentFields,
      resolveFieldOriginsForSave(latestComponentFields, values as FieldValues)
    )
  )
  return setComponentSubtree(latestFieldValues, {
    ...existingComponents,
    [componentId]: mergedComponentFields,
  })
}

/**
 * Removes state that must never be persisted: `documentsSearched` is transient
 * UI state, and a `null` `aiExtracted` is the pre-merge clear sentinel, dropped
 * so the PATCH deletes the key instead of writing a value older clients reject.
 */
function omitTransientFieldState(fieldValues: FieldValues): FieldValues {
  return Object.fromEntries(
    Object.entries(fieldValues).map(([fieldId, wrapper]) => {
      if (!wrapper) return [fieldId, wrapper]
      const persistable: FieldValue = omit(wrapper, 'documentsSearched')
      return [
        fieldId,
        persistable.aiExtracted === null
          ? omit(persistable, 'aiExtracted')
          : persistable,
      ]
    })
  )
}

/**
 * Field values as the save step would persist them, minus origin marks, so
 * dirty checks compare only real content changes against the saved ticket.
 */
export const toPersistedShape = (fieldValues: FieldValues): FieldValues =>
  omitTransientFieldState(omitOrigin(fieldValues))
